import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { InvitationIntro } from "@platform/features/invitation";
import { useInvitation, usePlatformStore } from "@platform/store";
import {
  useClaimInvitation,
  useCheckInvitationClaimStatus,
} from "@core/features/invitation";
import { Button } from "@themes/pollinator/ui/Button";

export function InvitationJoinPage() {
  const navigate = useNavigate();
  const user = usePlatformStore((s) => s.user);
  const { invitation, claimedVia } = useInvitation();
  const {
    mutate: claimInvitation,
    isPending,
    error,
    isError,
  } = useClaimInvitation();

  // Check if user has already claimed this invitation type for the program
  const { data: claimStatus } = useCheckInvitationClaimStatus({
    user,
    invitation,
  });
  const isEnrolled = claimStatus?.has_claimed_type === true;

  // If logged in and already enrolled, redirect to dashboard
  useEffect(() => {
    if (user && isEnrolled && invitation?.program_id) {
      navigate(`/dashboard/${invitation.program_id}`, { replace: true });
    }
  }, [user, isEnrolled, invitation?.program_id, navigate]);

  const handleStart = () => {
    if (!invitation?.shortcode) return;

    const quizId = invitation.display_options?.quiz_id;

    if (user) {
      if (quizId) {
        // Logged in + quiz configured: complete quiz first, then claim
        navigate(`/invitation/${invitation.shortcode}/fit`);
      } else {
        // Logged in + no quiz: claim directly
        claimInvitation({
          shortcode: invitation.shortcode,
          claimedVia: claimedVia ?? "direct_link",
        });
      }
    } else {
      if (quizId) {
        navigate(`/invitation/${invitation.shortcode}/fit`);
      } else {
        navigate(`/invitation/${invitation.shortcode}/signup`);
      }
    }
  };

  const loginButton = !user ? (
    <Button
      as={Link}
      to={`/login?redirect=/invitation/${invitation?.shortcode}/join`}
      theme="dark"
      variant="secondary"
      className="w-full md:w-fit min-w-24"
      data-testid="invitation-login-button"
    >
      Log In
    </Button>
  ) : null;

  const errorMessage = isError
    ? (error as Error)?.message || "Failed to claim invitation. Please try again."
    : null;

  return (
    <InvitationIntro
      onStart={handleStart}
      programName={invitation?.program_name ?? ""}
      buttonText={user ? "Join" : "Get Started"}
      description={
        !user
          ? "Join by creating a new account or log in if you already have an account on Pollinator."
          : undefined
      }
      isLoading={isPending}
      secondaryButton={loginButton}
      errorMessage={errorMessage}
    />
  );
}

export default InvitationJoinPage;
